
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Star, Award, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuraPoints } from '@/contexts/AuraPointsContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { getLevelInfo } from '@/models/AuraPoints';

interface AuraPointsSummaryProps {
  minimal?: boolean;
  showDetailed?: boolean;
}

const AuraPointsSummary: React.FC<AuraPointsSummaryProps> = ({ minimal = false, showDetailed = false }) => {
  const { points, loading } = useAuraPoints();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  if (loading) {
    return (
      <Card className={cn(
        "overflow-hidden border-foreground/10 shadow-lg",
        theme === 'dark' ? 'bg-midnight/80' : 'bg-white/95 backdrop-blur-sm'
      )}>
        <CardHeader>
          <CardTitle className="text-lg">Aura Points</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!points) {
    return (
      <Card className={cn(
        "overflow-hidden border-foreground/10 shadow-lg",
        theme === 'dark' ? 'bg-midnight/80' : 'bg-white/95 backdrop-blur-sm'
      )}>
        <CardHeader>
          <CardTitle className="text-lg">Aura Points</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-muted-foreground">Start engaging to earn Aura Points!</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const levelInfo = getLevelInfo(points.totalPoints);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="hardware-accelerated"
    >
      <Card className={cn(
        "overflow-hidden border-foreground/10 shadow-lg",
        theme === 'dark' 
          ? "bg-[#151833]/90 border-blue-900/50" 
          : "bg-white/95 backdrop-blur-md border-blue-100/50"
      )}>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-bold">Aura Points</CardTitle>
            {!minimal && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/points')}
                className="text-xs"
              >
                View Details
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div 
                className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold",
                  theme === 'dark' ? "bg-blue-900/30" : "bg-blue-100"
                )}
                style={{ color: levelInfo.color, borderColor: levelInfo.color }}
              >
                {levelInfo.level}
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-baseline">
                  <div className="font-medium">{levelInfo.name}</div>
                  <div className="text-sm font-semibold">{points.totalPoints} pts</div>
                </div>
                
                {levelInfo.nextLevel && (
                  <>
                    <div className="mt-2">
                      <Progress 
                        value={(levelInfo.progress.current / levelInfo.progress.target) * 100} 
                        className="h-2"
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Level {levelInfo.level}</span>
                      <span>{levelInfo.pointsToNextLevel} pts to Level {levelInfo.level + 1}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {(showDetailed || !minimal) && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className={cn(
                  "flex flex-col items-center p-2 rounded-md",
                  theme === 'dark' ? "bg-blue-900/20" : "bg-blue-50"
                )}>
                  <Star className="w-4 h-4 text-yellow-500 mb-1" />
                  <div className="text-sm font-medium">{points.averageRating.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground">Rating</div>
                </div>
                
                <div className={cn(
                  "flex flex-col items-center p-2 rounded-md",
                  theme === 'dark' ? "bg-blue-900/20" : "bg-blue-50"
                )}>
                  <Award className="w-4 h-4 text-purple-500 mb-1" />
                  <div className="text-sm font-medium">
                    {points.achievements?.filter(a => a.unlockedAt).length || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Achieved</div>
                </div>
                
                <div className={cn(
                  "flex flex-col items-center p-2 rounded-md",
                  theme === 'dark' ? "bg-blue-900/20" : "bg-blue-50"
                )}>
                  <TrendingUp className="w-4 h-4 text-green-500 mb-1" />
                  <div className="text-sm font-medium">{points.streak?.currentStreak || 0}</div>
                  <div className="text-xs text-muted-foreground">Streak</div>
                </div>
              </div>
            )}
            
            {minimal && (
              <div className="flex justify-center mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/points')}
                  className="text-xs w-full"
                >
                  View Aura Points
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AuraPointsSummary;
